import styled from "@emotion/styled"
import { useMediaQuery } from "@mui/material"

import { MainBannerHeroContent, MediaImageModel } from "@/api/generated/BMWInterfaces"
import { Desktop, Mobile } from "@/lib/common/MediaQueries"
import { allOffersClick, upperFindCarClick } from "@/lib/gtm"
import { useEffect, useState } from "react"
import "stories-react/dist/index.css"
import HeroContent from "./HeroContent"
import HeroControls from "./HeroControls"
import HeroCtaButton from "./HeroCtaButton"
import HeroMedia, { isVideo } from "./HeroMedia"

const StyledLayout = styled.div`
  width: 100%;
  height: calc(100vh - 60px);
  position: relative;
  ${(props) => props.theme.breakpoints.up("md")} {
    height: auto;
    aspect-ratio: 1280 / 540;
  }
  ${(props) => props.theme.breakpoints.up("xl")} {
    aspect-ratio: 1920 / 800;
  }
`

const StyledInner = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  padding: 24px 16px 40px 16px;
  flex-direction: column;
  align-items: center;
  gap: 40px;
  z-index: 2;
  position: relative;
  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 36px 40px 64px 40px;
    justify-content: center;
  }
`

const StyledContentButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;
  width: 100%;
  flex: 1;
  ${(props) => props.theme.breakpoints.up("md")} {
    gap: 64px;
    flex: unset;
  }
`

// Desktop controls are absolutely positioned against StyledInner, so the slot
// drops out of the flex flow there — otherwise it still claims a 64px gap.
const ControlsSlot = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  ${(props) => props.theme.breakpoints.up("md")} {
    display: contents;
  }
`

const ButtonGroup = styled.div<{ $single: boolean }>`
  display: flex;
  gap: 16px;
  width: 100%;
  justify-content: center;
  min-height: 40px;
  ${(props) => props.theme.breakpoints.up("md")} {
    gap: 24px;
    max-width: ${({ $single }) => ($single ? "328px" : "512px")};
  }
`

export type Story = {
  title: string
  subTitle: string
  logo: boolean
  url: string
}

const duration = 5000

type ResolveOptions = {
  videoFailed: boolean
  reducedMotion: boolean
}

const resolveMedia = (
  video: MediaImageModel | undefined,
  image: MediaImageModel | undefined,
  { videoFailed, reducedMotion }: ResolveOptions,
): MediaImageModel | undefined => {
  const preferImage = reducedMotion && !!image?.imageUrl
  const useVideo = !!video?.imageUrl && isVideo(video.imageUrl) && !videoFailed && !preferImage
  if (useVideo) return video
  if (image?.imageUrl) return image
  return video?.imageUrl ? video : undefined
}

type Props = {
  // Retained for the parent's call site (scroll-to-offers); the CTAs are now
  // link-based, so the hero no longer consumes it.
  handleStickyButtonClick?: () => void
  data: MainBannerHeroContent
}

export default function Hero({ data }: Props): React.ReactElement {
  const stories = data?.slides ?? []
  const [isPaused, setIsPaused] = useState(false)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isMediaReady, setIsMediaReady] = useState(false)
  const [videoFailed, setVideoFailed] = useState(false)
  const currentStory = stories[currentIndex]
  const prefersReducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)")
  // CTAs live on each slide, so they change as the carousel rotates.
  const primaryButton = data?.slides?.[currentIndex]?.primaryButton
  const secondaryButton = data?.slides?.[currentIndex]?.secondaryButton

  // A failed-video flag is per-slide, so clear it whenever the slide changes.
  useEffect(() => {
    setVideoFailed(false)
  }, [currentIndex])

  const resolveOptions = { videoFailed, reducedMotion: prefersReducedMotion }
  const desktopMedia = resolveMedia(
    currentStory?.videoDesktop,
    currentStory?.imageDesktop,
    resolveOptions,
  )
  // Rule #3: mobile owns its media only if it supplies EITHER a video or an
  // image; otherwise it inherits whatever desktop resolved to.
  const mobileHasOwnMedia =
    !!currentStory?.videoMobile?.imageUrl || !!currentStory?.imageMobile?.imageUrl
  const mobileMedia = mobileHasOwnMedia
    ? resolveMedia(currentStory?.videoMobile, currentStory?.imageMobile, resolveOptions)
    : desktopMedia

  // Handle story timing
  useEffect(() => {
    if (isPaused) return

    const isCurrentItemVideo = isVideo(desktopMedia?.imageUrl) || isVideo(mobileMedia?.imageUrl)
    if (!isMediaReady && isCurrentItemVideo) return

    const timer = setTimeout(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % stories.length)
    }, duration)

    return () => clearTimeout(timer)
  }, [
    currentIndex,
    isPaused,
    isMediaReady,
    stories.length,
    desktopMedia?.imageUrl,
    mobileMedia?.imageUrl,
  ])

  const handleMediaReady = (): void => {
    setIsMediaReady(true)
  }

  const goToNext = (): void => {
    setIsMediaReady(false)
    setCurrentIndex((prevIndex) => (prevIndex + 1) % stories.length)
  }

  const goToPrev = (): void => {
    setIsMediaReady(false)
    setCurrentIndex((prevIndex) => (prevIndex - 1 + stories.length) % stories.length)
  }

  const handleNext = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
    e.stopPropagation()
    goToNext()
  }

  const handlePrev = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
    e.stopPropagation()
    goToPrev()
  }

  // Arrows need somewhere to go; pause needs something moving — rotation
  // between banners and/or a video on the breakpoint actually being shown.
  const hasMultipleBanners = stories.length > 1
  const showPauseOnDesktop = hasMultipleBanners || isVideo(desktopMedia?.imageUrl)
  const showPauseOnMobile = hasMultipleBanners || isVideo(mobileMedia?.imageUrl)

  const handlePause = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
    e.stopPropagation()
    setIsPaused(true)
  }

  const handleResume = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
    e.stopPropagation()
    setIsPaused(false)
  }

  if (!currentStory) {
    return <></>
  }

  return (
    <StyledLayout onClick={goToNext}>
      <Desktop>
        {desktopMedia?.imageUrl && (
          <HeroMedia
            image={desktopMedia}
            index={currentIndex}
            playing={!isPaused && !prefersReducedMotion}
            onReady={handleMediaReady}
            onError={() => setVideoFailed(true)}
          />
        )}
      </Desktop>
      <Mobile>
        {mobileMedia?.imageUrl && (
          <HeroMedia
            image={mobileMedia}
            index={currentIndex}
            playing={!isPaused && !prefersReducedMotion}
            onReady={handleMediaReady}
            onError={() => setVideoFailed(true)}
          />
        )}
      </Mobile>
      <StyledInner>
        <StyledContentButtonWrapper>
          <HeroContent story={currentStory} />
          {
            <ButtonGroup
              $single={!(primaryButton?.isActive && secondaryButton?.isActive)}
              onClick={(e) => e.stopPropagation()}
            >
              {primaryButton?.isActive && (
                <HeroCtaButton
                  button={primaryButton}
                  fallbackHref="/"
                  onClick={upperFindCarClick}
                />
              )}
              {secondaryButton?.isActive && (
                <HeroCtaButton button={secondaryButton} fallbackHref="/" onClick={allOffersClick} />
              )}
            </ButtonGroup>
          }
        </StyledContentButtonWrapper>
        {(showPauseOnDesktop || showPauseOnMobile) && (
          <ControlsSlot>
            <HeroControls
              showArrows={hasMultipleBanners}
              showPauseOnDesktop={showPauseOnDesktop}
              showPauseOnMobile={showPauseOnMobile}
              handlePause={handlePause}
              handleResume={handleResume}
              handlePrev={handlePrev}
              handleNext={handleNext}
              isPaused={isPaused}
            />
          </ControlsSlot>
        )}
      </StyledInner>
    </StyledLayout>
  )
}
